import React, { useState } from 'react';
import { syncApi } from '../../services/api';
import { Eye, Play, Loader, CheckCircle, XCircle } from 'lucide-react';

const ViewSyncPage = () => {
  const [viewSyncing, setViewSyncing] = useState(false);
  const [viewSyncResult, setViewSyncResult] = useState(null);

  const runViewSync = async () => {
    setViewSyncing(true);
    setViewSyncResult(null);
    try {
      const res = await syncApi.syncViews();
      setViewSyncResult({ success: true, data: res.data });
    } catch (err) {
      setViewSyncResult({ success: false, error: err.response?.data?.error || err.message });
    }
    setViewSyncing(false);
  };

  return (
    <div className="dev-module view-sync-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Eye size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            View Synchronization
          </h1>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-step-panel" style={{ maxWidth: '800px' }}>
          <div className="import-step-header">
            <span className="import-step-number">1</span>
            <h3>Sync Views</h3>
          </div>
          <p className="import-step-desc">
            Push all views from the template database to all project databases.
            Add a new project DB to <code>ViewSync:Targets</code> in appsettings — no code changes needed.
          </p>

          <button
            className="import-execute-btn"
            onClick={runViewSync}
            disabled={viewSyncing}
            style={{ marginTop: '8px' }}
          >
            {viewSyncing
              ? <><Loader size={16} className="spinning" /> Syncing Views...</>
              : <><Play size={16} /> Sync Views</>}
          </button>

          {viewSyncResult && (
            <div style={{ marginTop: '16px' }}>
              {viewSyncResult.success ? (
                <>
                  <div className={`sync-val-result ${viewSyncResult.data.totalFail === 0 ? 'success' : 'failed'}`}>
                    <div className="sync-val-summary">
                      {viewSyncResult.data.totalFail === 0 ? (
                        <><CheckCircle size={18} /><span>{viewSyncResult.data.viewCount} views synced to {viewSyncResult.data.targets.length} databases</span></>
                      ) : (
                        <><XCircle size={18} /><span>{viewSyncResult.data.totalFail} failures — check results below</span></>
                      )}
                    </div>
                  </div>

                  <div className="sync-history-table-wrap" style={{ marginTop: '12px' }}>
                    <table className="sync-history-table">
                      <thead>
                        <tr>
                          <th>View</th>
                          {viewSyncResult.data.targets.map(t => <th key={t} className="center">{t}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {viewSyncResult.data.viewCount > 0 && Object.values(viewSyncResult.data.results)[0].map((v, i) => (
                          <tr key={i}>
                            <td>{v.name}</td>
                            {viewSyncResult.data.targets.map(target => {
                              const r = viewSyncResult.data.results[target]?.[i];
                              return (
                                <td key={target} className="center" title={r?.error || ''}>
                                  {r?.success
                                    ? <CheckCircle size={14} className="icon-success" />
                                    : <XCircle size={14} className="icon-fail" />}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="sync-val-result failed">
                  <div className="sync-val-summary">
                    <XCircle size={18} /><span>View sync failed: {viewSyncResult.error}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ViewSyncPage;
